import { useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup, EditableCollectionBinding, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, stagger, toParagraph } from '../utils/quinn';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { useTourModal } from '../utils/details';
import { embedFor, gatherTours, isVideoUrl, normName, tourKey, type GatheredTour, type RawTour } from '../utils/tours';
import { PLAN_OUTLINE } from './FloorplansList';

const extraTourDef: SlotGroupDefinition = {
  label: 'Your Own Tour',
  description: 'A tour link that is not in the property data: a Matterport, a 360 viewer, or a YouTube or Vimeo walkthrough. Shown after the property\'s tours.',
  fields: {
    title: { label: 'Tour Name', type: 'text', defaultValue: 'Video walkthrough' },
    url: { label: 'Tour URL', type: 'url', description: 'Leave empty to show only the property\'s tours.', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

/** "Step inside the Jarvis and the Jefferson from anywhere, ..." — names the plans that have a tour. */
function leadFor(names: string[]): string {
  const tail = 'Pick a tour, then look around at your own pace.';
  if (!names.length) return `Walk through the community from anywhere. ${tail}`;
  const list = names.length === 1 ? `the ${names[0]}` : `${names.slice(0, -1).map((n) => `the ${n}`).join(', ')} and the ${names[names.length - 1]}`;
  return `Step inside ${list} from anywhere. ${tail}`;
}

/**
 * Every virtual tour the property has, played inline: the property's tour list, tours on individual units,
 * tour links in custom fields, and one an editor adds. The frame loads only once a visitor starts a tour.
 */
export function VirtualTourEmbed() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const modal = useTourModal();
  const propertyTours = useCollectionBinding<RawTour>('virtualtourembed.tours', { collection: 'virtual_tours' });
  const extra = useSlotGroup('virtualtourembed.extra_tour', extraTourDef);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [started, setStarted] = useState(false);

  const plans = (p.floorplans as Array<{ floorplan_name?: string; floorplan_image_url?: string }>) || [];
  const featured = clean(p.property_featured_image);
  const tours: GatheredTour[] = gatherTours(p, propertyTours);
  const extraUrl = clean(extra.url);
  if (extraUrl && /^https?:\/\//i.test(extraUrl) && !tours.some((t) => t.key === tourKey(extraUrl))) {
    tours.push({
      key: tourKey(extraUrl),
      title: clean(extra.title) || 'Video walkthrough',
      url: extraUrl,
      thumb: null,
      desc: null,
      where: 'Community',
      origin: 'editor',
      planName: null,
      isVideo: isVideoUrl(extraUrl),
    });
  }

  const planImage = (t: GatheredTour) => clean(plans.find((f) => t.planName && normName(f.floorplan_name) === normName(t.planName))?.floorplan_image_url);
  const imageFor = (t: GatheredTour) => {
    if (t.thumb) return { url: t.thumb, photo: true };
    const plan = planImage(t);
    if (plan) return { url: plan, photo: false };
    return featured ? { url: featured, photo: true } : null;
  };

  if (!tours.length && !editing) return null;

  const active = tours.find((t) => t.key === activeKey) || tours[0];
  const embed = active ? embedFor(active.url, true) : null;
  const poster = active ? imageFor(active) : null;
  const planNames = [...new Set(tours.map((t) => t.planName).filter((n): n is string => !!n))];
  const leadDefault = toParagraph(leadFor(planNames));
  const showList = tours.length > 1 || editing;
  const choose = (t: GatheredTour) => {
    setActiveKey(t.key);
    setStarted(true);
  };

  const item = (t: GatheredTour, i: number) => {
    const img = imageFor(t);
    const isActive = t.key === active?.key;
    return (
      <button
        key={t.key}
        type="button"
        className="vtembed__item"
        aria-pressed={isActive}
        onClick={() => choose(t)}
        data-vibesite-event="virtual_tour_clicked"
        data-reveal
        style={stagger(i)}
      >
        <span className={`vtembed__thumb${img?.photo ? ' vtembed__thumb--photo' : ''}`}>
          {img ? <VibesitesImage url={img.url} alt="" sizes="96px" /> : PLAN_OUTLINE}
        </span>
        <span className="vtembed__item-t">
          <span className="vtembed__item-name">{t.title}</span>
          {t.where && <span className="vtembed__item-where">{t.where}</span>}
        </span>
        <span className="vtembed__badge vtembed__badge--sm" aria-hidden="true">{t.isVideo ? 'Video' : '3D'}</span>
      </button>
    );
  };

  const propertyItems = tours.filter((t) => t.origin !== 'editor');
  const editorItem = tours.find((t) => t.origin === 'editor');

  return (
    <section className="section vtembed" id="tours">
      <div className="vtembed__head" data-reveal>
        <div className="vtembed__intro">
          <RichTextContent path="virtualtourembed.eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="virtualtourembed.title" as="h2" className="h2" />
        </div>
        <RichTextContent path="virtualtourembed.lead" as="p" className="vtembed__lead" defaultValue={leadDefault} />
      </div>

      <div className={`vtembed__body${showList ? '' : ' vtembed__body--solo'}`}>
        <div className="vtembed__player" data-reveal>
          <div className="vtembed__stage">
            {!active ? (
              <div className="vtembed__empty">No virtual tours on this property yet. Add one to the property, or paste a link under Your Own Tour.</div>
            ) : started && embed?.kind === 'video' ? (
              <video key={active.key} className="vtembed__media" src={embed.src} controls autoPlay playsInline />
            ) : started && embed ? (
              <iframe
                key={active.key}
                className="vtembed__media"
                src={embed.src}
                title={active.title}
                allow="fullscreen; autoplay; xr-spatial-tracking; gyroscope; accelerometer; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <button
                type="button"
                className="vtembed__poster"
                onClick={() => setStarted(true)}
                aria-label={`${active.isVideo ? 'Play the video' : 'Start the 3D tour'}: ${active.title}`}
                data-vibesite-event="virtual_tour_clicked"
              >
                <span className={`vtembed__poster-img${poster?.photo ? ' vtembed__poster-img--photo' : ''}`}>
                  {poster ? <VibesitesImage url={poster.url} alt="" sizes="(max-width: 991px) 100vw, 66vw" /> : PLAN_OUTLINE}
                </span>
                <span className="vtembed__start">
                  <span className="vtembed__badge" aria-hidden="true">{active.isVideo ? 'Video' : '3D'}</span>
                  <span className="vtembed__start-label">{active.isVideo ? 'Play the video' : 'Start the 3D tour'}</span>
                </span>
              </button>
            )}
          </div>
          {active && (
            <div className="vtembed__caption">
              <div className="vtembed__now">
                <span className="vtembed__name">{active.title}</span>
                {(active.desc || active.where) && <span className="vtembed__desc">{active.desc || active.where}</span>}
              </div>
              <div className="vtembed__actions">
                <button type="button" className="textlink" data-vibesite-event="virtual_tour_clicked" onClick={modal.open(embedFor(active.url, true).src, active.title)}>
                  Full screen
                </button>
                <a className="textlink" href={active.url} target={active.origin === 'editor' ? extra._target || '_blank' : '_blank'} rel="noopener" data-vibesite-event="virtual_tour_clicked">
                  Open in a new tab ↗
                </a>
              </div>
            </div>
          )}
        </div>

        {showList && (
          <div className="vtembed__side">
            <div className="vtembed__count">{tours.length === 1 ? '1 tour' : `${tours.length} tours`}</div>
            <EditableCollectionBinding bindingPath="virtualtourembed.tours" label="Virtual Tours" className="vtembed__list">
              {propertyItems.map(item)}
            </EditableCollectionBinding>
            <EditableSlotGroup groupPath="virtualtourembed.extra_tour" definition={extraTourDef}>
              {editorItem ? (
                <div className="vtembed__list">{item(editorItem, propertyItems.length)}</div>
              ) : editing ? (
                <div className="vtembed__add">+ Add your own tour link</div>
              ) : null}
            </EditableSlotGroup>
          </div>
        )}
      </div>
      {modal.node}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour Embed',
  tokens: {
    'virtualtourembed.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Look around</p>' },
    'virtualtourembed.title': { label: 'Title', type: 'richtext', defaultValue: '<p>Tour from anywhere.</p>' },
    'virtualtourembed.lead': {
      label: 'Lead',
      description: 'Until edited, names the floor plans that have a tour.',
      type: 'richtext',
      defaultValue: '<p>Walk through the community from anywhere. Pick a tour, then look around at your own pace.</p>',
      generator: {
        prompt: 'One sentence introducing the virtual tours this property actually has (which floor plans, units, or community spaces they walk through), inviting the reader to pick one.',
        context: 'Lead of the tour player on the Virtual Tours page, beside the embedded tour and the list of tours to switch between.',
        length: '1 sentence',
      },
    },
  },
  slotGroups: {
    'virtualtourembed.extra_tour': extraTourDef,
  },
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default VirtualTourEmbed;
